import { useState } from 'react'
import { useMutation } from 'react-query'
import type { UseMutateAsyncFunction } from 'react-query'

import {
  validateDescription,
  validateExit,
  validateInstructions,
} from 'api/scenarios'

import { configByType } from './constants'

interface ValidationObject {
  prompt: string
  response?: string
  replyGuidelines?: string
  examples?: string[]
  validationResponseDate: Date
}

interface Response {
  response: string
  replyGuidelines?: string
  examples?: string[]
}

export const useValidations = ({
  type,
  onSuccess,
  onError,
}: {
  type: keyof typeof configByType
  onSuccess: () => void
  onError: () => void
}) => {
  const [validations, setValidations] = useState<ValidationObject[]>([])

  const handleSuccess = ({
    data,
    promptInput,
  }: {
    data: Response
    promptInput: string
  }) => {
    const { response, replyGuidelines, examples } = data
    setValidations([
      ...validations,
      {
        prompt: promptInput,
        response,
        replyGuidelines,
        examples,
        validationResponseDate: new Date(),
      },
    ])
    onSuccess()
  }

  const onValidateInstructions = useMutation(validateInstructions, {
    onSuccess: ({ data }, promptInput) =>
      handleSuccess({
        data,
        promptInput,
      }),
    onError,
  })

  const onValidateDescription = useMutation(validateDescription, {
    onSuccess: ({ data }, promptInput) =>
      handleSuccess({
        data,
        promptInput,
      }),
    onError,
  })

  const onValidateExit = useMutation(validateExit, {
    onSuccess: ({ data }, promptInput) =>
      handleSuccess({
        data,
        promptInput,
      }),
    onError,
  })

  const validateByType: {
    [K in keyof typeof configByType]: UseMutateAsyncFunction<
      unknown,
      unknown,
      string,
      unknown
    >
  } = {
    instructions: onValidateInstructions.mutateAsync,
    description: onValidateDescription.mutateAsync,
    exit: onValidateExit.mutateAsync,
    guardrailReplyGuidelines: onValidateInstructions.mutateAsync,
  }

  return {
    validations,
    validate: validateByType[type],
  }
}
